import React, { useEffect, useState } from "react";
import { formatPrice } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

// Mirror of ORDER_STATUS_LABELS in backend/server.py + STATUSES in
// AdminOrders.jsx. Keep these three in sync — they're the customer-,
// back-office-, and email-facing labels for the same internal status.
const STATUS_LABEL = {
  pending_payment: "En attente de paiement",
  paid:            "En cours de préparation",
  shipped:         "Expédiée",
  delivered:       "Livrée",
  cancelled:       "Annulée",
  refunded:        "Remboursée",
};

const EMPTY_ADDR = { line1: "", line2: "", postal_code: "", city: "", country: "France" };

export default function MyAccount() {
  const { user, setUser, logout, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [tab, setTab] = useState("orders");
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [shippingDifferent, setShippingDifferent] = useState(false);

  useEffect(() => {
    if (!user) return;
    const billing = { ...EMPTY_ADDR, ...(user.billing_address || {}) };
    const shipping = user.shipping_address || null;
    setProfile({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      phone: user.phone || "",
      billing_address: billing,
      shipping_address: shipping ? { ...EMPTY_ADDR, ...shipping } : { ...billing },
    });
    setShippingDifferent(Boolean(shipping));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(r => setOrders(r.data));
    api.get("/returns").then(r => setReturns(r.data));
  }, [user]);

  if (loading) return <div className="p-12">…</div>;
  if (!user) return <Navigate to="/connexion" replace/>;

  const requestReturn = async (order) => {
    const reason = prompt("Motif du retour ?");
    if (!reason) return;
    try {
      await api.post("/returns", { order_id: order.id, items: order.items.map(it => ({ product_id: it.product_id, size: it.size, quantity: it.quantity })), reason });
      toast.success("Demande envoyée. Nous reviendrons vers vous sous 48h ✿");
      api.get("/returns").then(r => setReturns(r.data));
    } catch (e) { toast.error("Erreur"); }
  };

  const retryPayment = async (order) => {
    setBusyOrderId(order.id);
    try {
      const { data } = await api.post(`/orders/${order.id}/retry-payment`);
      window.location.href = data.payment_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de relancer le paiement");
      setBusyOrderId(null);
    }
  };

  const saveProfile = async () => {
    if (!profile) return;
    setSavingProfile(true);
    try {
      const payload = {
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim(),
        phone: profile.phone.trim(),
        billing_address: profile.billing_address,
        shipping_address: shippingDifferent ? profile.shipping_address : null,
      };
      const { data } = await api.put("/auth/me", payload);
      setUser(data);
      toast.success("Infos enregistrées ✿");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingProfile(false);
    }
  };

  const downloadInvoice = async (order) => {
    try {
      const { data } = await api.get(`/orders/${order.id}/invoice`);
      // Pop a tab so the browser handles the PDF (preview + save) rather
      // than us trying to programmatically download via blob.
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Facture indisponible");
    }
  };

  const cancelOrder = async (order) => {
    if (!window.confirm(`Annuler la commande ${order.order_number} ?`)) return;
    setBusyOrderId(order.id);
    try {
      await api.post(`/orders/${order.id}/cancel`);
      toast.success("Commande annulée");
      api.get("/orders").then(r => setOrders(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusyOrderId(null);
    }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-16">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="overline text-berulia-rosedeep">Espace client</p>
          <h1 className="font-heading text-5xl mt-2">Bonjour <span className="font-serif-italic">{user.first_name}</span></h1>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="overline link-underline">Se déconnecter</button>
      </div>

      <div className="flex gap-6 border-b border-berulia-border mb-8 flex-wrap">
        <button onClick={()=>setTab("orders")} data-testid="tab-orders" className={`overline pb-3 border-b-2 ${tab==="orders" ? "border-berulia-ink" : "border-transparent text-berulia-inkmuted"}`}>Mes commandes</button>
        <button onClick={()=>setTab("returns")} data-testid="tab-returns" className={`overline pb-3 border-b-2 ${tab==="returns" ? "border-berulia-ink" : "border-transparent text-berulia-inkmuted"}`}>Retours</button>
        <button onClick={()=>setTab("profile")} data-testid="tab-profile" className={`overline pb-3 border-b-2 ${tab==="profile" ? "border-berulia-ink" : "border-transparent text-berulia-inkmuted"}`}>Mes infos</button>
      </div>

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 && <p className="text-berulia-inkmuted">Aucune commande pour l'instant.</p>}
          {orders.map(o => (
            <div key={o.id} className="bg-white border border-berulia-border p-6" data-testid={`order-${o.order_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="overline">{new Date(o.created_at).toLocaleDateString("fr-FR")} · N° {o.order_number}</p>
                  <p className="font-heading text-2xl mt-1">{formatPrice(o.total)}</p>
                </div>
                <div className="text-right">
                  <span className="overline px-3 py-1 bg-berulia-beigewarm">{STATUS_LABEL[o.status] || o.status}</span>
                  {o.tracking_number && <p className="text-xs text-berulia-inkmuted mt-2">Suivi {o.carrier}: <strong>{o.tracking_number}</strong></p>}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                {o.items.map((it,i)=>(<div key={i} className="flex gap-3 text-sm"><img src={it.image} className="w-14 h-18 object-cover bg-berulia-beigewarm" alt=""/><div><p className="font-heading text-base">{it.name}</p><p className="text-xs text-berulia-inkmuted">{it.size} × {it.quantity}</p></div></div>))}
              </div>
              {o.status === "pending_payment" && (
                <div className="mt-5 pt-4 border-t border-berulia-border flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => retryPayment(o)}
                    disabled={busyOrderId === o.id}
                    data-testid={`pay-${o.id}`}
                    className="bg-berulia-rosedeep text-berulia-inkdeep overline px-6 py-3 hover:bg-berulia-rose transition disabled:opacity-50"
                  >
                    {busyOrderId === o.id ? "Redirection…" : "Payer maintenant →"}
                  </button>
                  <button
                    onClick={() => cancelOrder(o)}
                    disabled={busyOrderId === o.id}
                    data-testid={`cancel-${o.id}`}
                    className="overline text-sm text-berulia-inkmuted hover:text-berulia-inkdeep disabled:opacity-50"
                  >
                    Annuler la commande
                  </button>
                </div>
              )}
              {/* Invoice download: prefer the BERULIA PDF (invoice_s3_key)
                  for orders placed after #61. Older paid orders only carry
                  the Stripe-hosted URL (#60) — keep that as a fallback so
                  we don't strand pre-existing customers without a facture. */}
              {(o.invoice_s3_key || o.invoice_url) && o.status !== "pending_payment" && (
                <div className="mt-4">
                  {o.invoice_s3_key ? (
                    <button
                      onClick={() => downloadInvoice(o)}
                      data-testid={`invoice-${o.id}`}
                      className="overline link-underline text-sm"
                    >
                      Télécharger la facture →
                    </button>
                  ) : (
                    <a
                      href={o.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`invoice-${o.id}`}
                      className="overline link-underline text-sm"
                    >
                      Télécharger la facture →
                    </a>
                  )}
                </div>
              )}
              {o.status === "delivered" && <button onClick={()=>requestReturn(o)} data-testid={`return-${o.id}`} className="mt-4 overline link-underline">Demander un retour →</button>}
            </div>
          ))}
        </div>
      )}

      {tab === "returns" && (
        <div className="space-y-3">
          {returns.length === 0 && <p className="text-berulia-inkmuted">Aucune demande de retour.</p>}
          {returns.map(r => (
            <div key={r.id} className="bg-white border border-berulia-border p-5 flex justify-between">
              <div><p className="overline">{new Date(r.created_at).toLocaleDateString("fr-FR")} · Commande {r.order_number}</p><p className="mt-1">{r.reason}</p></div>
              <span className="overline px-3 py-1 bg-berulia-beigewarm h-fit">{r.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "profile" && profile && (
        <div className="max-w-2xl space-y-8">
          <section className="bg-white border border-berulia-border p-6 space-y-4">
            <h2 className="font-heading text-2xl">Identité</h2>
            <p className="text-xs text-berulia-inkmuted">L'adresse email <strong>{user.email}</strong> sert d'identifiant et n'est pas modifiable.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="overline text-berulia-inkmuted">Prénom</span>
                <input value={profile.first_name} onChange={e=>setProfile(p=>({...p, first_name:e.target.value}))} data-testid="profile-first-name" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
              <label className="block">
                <span className="overline text-berulia-inkmuted">Nom</span>
                <input value={profile.last_name} onChange={e=>setProfile(p=>({...p, last_name:e.target.value}))} data-testid="profile-last-name" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-berulia-inkmuted">Téléphone</span>
                <input value={profile.phone} onChange={e=>setProfile(p=>({...p, phone:e.target.value}))} data-testid="profile-phone" placeholder="06 12 34 56 78" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
            </div>
          </section>

          <section className="bg-white border border-berulia-border p-6 space-y-4">
            <h2 className="font-heading text-2xl">Adresse de facturation</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block sm:col-span-2">
                <span className="overline text-berulia-inkmuted">Adresse</span>
                <input value={profile.billing_address.line1} onChange={e=>setProfile(p=>({...p, billing_address:{...p.billing_address, line1:e.target.value}}))} data-testid="profile-billing-line1" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-berulia-inkmuted">Complément (optionnel)</span>
                <input value={profile.billing_address.line2} onChange={e=>setProfile(p=>({...p, billing_address:{...p.billing_address, line2:e.target.value}}))} data-testid="profile-billing-line2" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
              <label className="block">
                <span className="overline text-berulia-inkmuted">Code postal</span>
                <input value={profile.billing_address.postal_code} onChange={e=>setProfile(p=>({...p, billing_address:{...p.billing_address, postal_code:e.target.value}}))} data-testid="profile-billing-postal" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
              <label className="block">
                <span className="overline text-berulia-inkmuted">Ville</span>
                <input value={profile.billing_address.city} onChange={e=>setProfile(p=>({...p, billing_address:{...p.billing_address, city:e.target.value}}))} data-testid="profile-billing-city" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
              </label>
            </div>
          </section>

          <section className="bg-white border border-berulia-border p-6 space-y-4">
            <h2 className="font-heading text-2xl">Adresse de livraison</h2>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={shippingDifferent} onChange={e=>setShippingDifferent(e.target.checked)} data-testid="profile-shipping-different"/>
              Utiliser une adresse de livraison différente
            </label>
            {shippingDifferent && (
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block sm:col-span-2">
                  <span className="overline text-berulia-inkmuted">Adresse</span>
                  <input value={profile.shipping_address.line1} onChange={e=>setProfile(p=>({...p, shipping_address:{...p.shipping_address, line1:e.target.value}}))} data-testid="profile-shipping-line1" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
                </label>
                <label className="block sm:col-span-2">
                  <span className="overline text-berulia-inkmuted">Complément (optionnel)</span>
                  <input value={profile.shipping_address.line2} onChange={e=>setProfile(p=>({...p, shipping_address:{...p.shipping_address, line2:e.target.value}}))} data-testid="profile-shipping-line2" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
                </label>
                <label className="block">
                  <span className="overline text-berulia-inkmuted">Code postal</span>
                  <input value={profile.shipping_address.postal_code} onChange={e=>setProfile(p=>({...p, shipping_address:{...p.shipping_address, postal_code:e.target.value}}))} data-testid="profile-shipping-postal" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
                </label>
                <label className="block">
                  <span className="overline text-berulia-inkmuted">Ville</span>
                  <input value={profile.shipping_address.city} onChange={e=>setProfile(p=>({...p, shipping_address:{...p.shipping_address, city:e.target.value}}))} data-testid="profile-shipping-city" className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"/>
                </label>
              </div>
            )}
            {!shippingDifferent && <p className="text-xs text-berulia-inkmuted">Vos colis seront livrés à l'adresse de facturation.</p>}
          </section>

          <button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save" className="bg-berulia-rosedeep text-berulia-inkdeep overline px-8 py-4 hover:bg-berulia-rose transition disabled:opacity-50">
            {savingProfile ? "Enregistrement…" : "Enregistrer mes infos"}
          </button>
        </div>
      )}
    </div>
  );
}
